import {
  Component,
  Input,
  Output,
  OnInit,
  OnChanges,
  SimpleChanges,
  HostListener,
  EventEmitter,
  TemplateRef,
  ViewEncapsulation
} from '@angular/core';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TableComponent implements OnInit, OnChanges {
  @Input() tableCellTemplate: TemplateRef<unknown>;
  @Input() rows: any;
  @Input() columns: any;
  @Input() settings: any;
  @Input() hasActions: false;
  @Input() hideMenu: false;
  @Input() tallTable: false;
  @Output() deleted = new EventEmitter<Object>();
  @HostListener('document:click', ['$event.target']) public onClick(target) {
    // show / close row menu
    if (typeof target.className === 'string' && target.className.includes('edit-delete-button')) {
      let rect = target.getBoundingClientRect();
      let menu = document.getElementsByClassName('settings-menu') as HTMLCollectionOf<HTMLElement>;
      menu[0].style.top = rect.top + 'px';
    } else {
      this.showingRowMenu = false;
    }
  }
  showingRowMenu = false;
  showingDetails = false;
  selected;
  details = [];
  searching = true;
  query;
  hasBeenFiltered;
  originalRows;

  // pagination vars
  start = 0;
  numberOfRows = {
    name: '25',
    value: 25
  };
  sortHistory = [];

  constructor(private router: Router, private route: ActivatedRoute) {}

  ngOnInit(): void {
    let rowsDisplayed = window.sessionStorage.getItem('rows');
    this.numberOfRows = {
      name: rowsDisplayed || '25',
      value: Number(rowsDisplayed) || 25
    };
    let start = Number(window.sessionStorage.getItem('page'));
    this.start = start;

    // this orders the table on first load
    this.columns[0].descendingOrder = 1;
    this.sort(this.columns[0]);
  }

  ngOnChanges(change: SimpleChanges) {
    if (change.rows) {
      this.query = '';
      this.rows = change.rows.currentValue;
      this.originalRows = this.rows;
      if (this.start > this.rows.length) {
        this.start = 0;
      }
      this.sortHistory = JSON.parse(window.sessionStorage.getItem('sortHistory'));
      if (this.sortHistory !== null) {
        // this block sets up the column in sortHistory so that it gets sorted correctly on it's first click
        this.columns.forEach((column) => {
          let match = this.sortHistory.filter((sortedColumn) => {
            return sortedColumn.key === column.key;
          });
          if (match[0]) {
            column.descendingOrder = !match[0].descendingOrder;
          }
        });
        this.sort({
          key: this.sortHistory[0].key,
          descendingOrder: this.sortHistory[0].descendingOrder,
          type: this.columns[0].type || 'string'
        });
      }

      // ensure every table cell has a sortable value
      this.rows.forEach((row) => {
        this.columns.forEach((column) => {
          if (row[column.key] === undefined || row[column.key] === null) {
            row[column.key] = '';
          }
        });
      });
    }
  }

  rowEnter = (event: any, row: any) => {
    if (event.key === 'Enter' && event.target.id !== 'meataball' && event.target.id !== 'deletebutton') {
      this.edit(row);
    }
  }

  showRowMenu = (row, index) => {
    this.showingRowMenu = index;
  };

  getCellData = (column, row) => {
    let displayValue = row[column.key];
    if (column.friendly) {
      displayValue = row[column.friendly];
    }
    let data = {
      value: displayValue,
      column,
      row
    };
    return data;
  };

  edit = (row) => {
    let id = '';
    if (row.id) {
      id = row.id;
    } else if (row.incidentId) {
      id = row.incidentId;
    } else if (row.href) {
      id = row.href.slice(row.href.lastIndexOf('/') + 1, row.href.length);
    } else if (row.personnelId) {
      id = row.personnelId;
    }

    window.sessionStorage.setItem('rows', this.numberOfRows.value.toString());
    window.sessionStorage.setItem('page', this.start.toString());

    if (this.route.snapshot.routeConfig.path.includes('mars')
        || this.route.snapshot.routeConfig.path.includes('pre2004jumps')) {
      this.router.navigate([this.settings.route + '/' + id]);
    } else {
      this.router.navigate([this.settings.route + '/' + id, row]);
    }
  };

  delete = (row) => {
    this.deleted.emit(row);
  };

  new = () => {
    this.router.navigate([this.settings.route + '/new']);
  };

  sort = (column) => {
    // set all other columns to default order
    this.columns.forEach((col) => {
      if (col.key !== column.key) {
        this.rows.sort((a, b) => {
          var keyA = a[col.key]?.toString();
          var keyB = b[col.key]?.toString();
          if (keyA?.toLowerCase() < keyB?.toLowerCase()) {
            return -1;
          }
          if (keyA?.toLowerCase() > keyB?.toLowerCase()) {
            return 1;
          }
          return 0;
        });
      }
    });

    // sort selected column
    if (column.type !== 'date' && column.type !== 'number') {
      this.rows.sort((a, b) => {
        var keyA = a[column.key]?.toString();
        var keyB = b[column.key]?.toString();
        if (keyA?.toLowerCase() < keyB?.toLowerCase()) {
          return column.descendingOrder ? 1 : -1;
        }
        if (keyA?.toLowerCase() > keyB?.toLowerCase()) {
          return column.descendingOrder ? -1 : 1;
        }
        return 0;
      });
    }
    
    // if the column type is date, sort by date
    if (column.type === 'date') {
      this.rows.sort((a, b) => {
        const keyA = (new Date(a[column.key])).valueOf();
        const keyB = (new Date(b[column.key])).valueOf();
        if (keyA < keyB) {
          return column.descendingOrder ? 1 : -1;
        }
        if (keyA > keyB) {
          return column.descendingOrder ? -1 : 1;
        }
        return 0;
      });
    }

    // if the column type is number, sort by number
    if (column.type === 'number') {
      this.rows.sort((a, b) => {
        const keyA = Number(a[column.key]);
        const keyB = Number(b[column.key]);
        if (keyA < keyB) {
          return column.descendingOrder ? 1 : -1;
        }
        if (keyA > keyB) {
          return column.descendingOrder ? -1 : 1;
        }
        return 0;
      });
    }

    this.sortHistory = [
      {
        key: column.key,
        descendingOrder: column.descendingOrder
      }
    ];

    window.sessionStorage.setItem('sortHistory', JSON.stringify(this.sortHistory));

    column.descendingOrder = !column.descendingOrder;
  };

  onKey = (event) => {
    if (!this.searching) {
      this.query = null;
      return;
    }
    // always filter against the original set of rows
    if (!this.hasBeenFiltered) {
      this.hasBeenFiltered = true;
      this.originalRows = this.rows;
    }
    this.rows = this.originalRows;

    // find rows that match query
    // **
    // this implementation will filter rows based on what columns are added to the table
    // loop through the keys in the row instead to filter against all the row data instead of just what's shown in the columns fields
    let filteredRows = [];
    for (const key in this.columns) {
      if (this.columns.hasOwnProperty(key)) {
        let column = this.columns[key];
        let colKey = column.key;
        let friendlyKey = column.friendly;
        this.rows.forEach((row) => {
          if (!row[colKey]) {
            return;
          }
          let value = row[colKey].toString().toLowerCase();
          let friendlyValue = row[friendlyKey]?.toString().toLowerCase();
          if (
            value.includes(this.query.toLowerCase()) ||
            friendlyValue?.includes(this.query.toLowerCase())
          ) {
            filteredRows.push(row);
          }
        });
      }
    }

    // dedupe rows
    let uniqueRows = [...new Set(filteredRows)];
    this.rows = uniqueRows;
  };

  selectRow = (row) => {
    this.details = [];
    for (const key in row) {
      if (row.hasOwnProperty(key)) {
        this.details.push({ key: key, value: row[key] });
      }
    }
    this.selected = row;
  };

  getPageRatio = () => {
    const totalNumberOfRows = this.rows.length;
    const numberOfRowsPerPage = this.numberOfRows.value;
    const currentPage = Math.ceil(this.start / numberOfRowsPerPage) + 1;
    const totalPages = Math.ceil(totalNumberOfRows / numberOfRowsPerPage);
    return `${currentPage}/${totalPages}`;
    
  }

  pageLeft = () => {
    this.start = this.start - this.numberOfRows.value;
    if (this.start < 0) {
      this.start = 0;
    }
  };

  pageRight = () => {
    if (this.start + this.numberOfRows.value * 2 >= this.rows.length) {
      this.start = this.rows.length - this.numberOfRows.value;
      if (this.start < 0) {
        this.start = 0;
      }
    } else {
      this.start = this.start + this.numberOfRows.value;
    }
  };
}
